import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// HTTP statuses that indicate a transient edge/origin failure where a silent
// retry has a good chance of succeeding (Cloudflare 5xx family + 408).
const RETRY_STATUSES = new Set([408, 502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527]);
const RETRY_METHODS  = new Set(["get", "head", "options"]); // never retry mutating verbs

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 25_000, // hard timeout so the UI never freezes on a stalled origin
  // We force responseType "json" so axios at least *attempts* to parse the body
  // as JSON. When the body is Cloudflare HTML (5xx) the parse fails and the
  // raw string ends up on err.response.data — our error formatter handles that.
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("tt_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Transparent retry for transient Cloudflare/origin 5xx errors so the user
// never sees the raw "The origin web server sent a response that Cloudflare
// could not parse" toast. Two retries with exponential backoff (300ms, 900ms)
// only for idempotent GET/HEAD/OPTIONS requests.
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err?.config;
    const status = err?.response?.status;
    const method = (cfg?.method || "get").toLowerCase();
    if (!cfg || !RETRY_METHODS.has(method)) return Promise.reject(err);
    cfg.__retryCount = cfg.__retryCount || 0;
    const isNetworkErr = !err.response;
    const shouldRetry = (isNetworkErr || (status && RETRY_STATUSES.has(status))) && cfg.__retryCount < 2;
    if (!shouldRetry) return Promise.reject(err);
    cfg.__retryCount += 1;
    const delay = 300 * Math.pow(3, cfg.__retryCount - 1); // 300, 900
    await new Promise((r) => setTimeout(r, delay));
    return api.request(cfg);
  },
);

/**
 * Convert any axios/fetch failure into a clean, user-readable Turkish string.
 * Never bubbles up HTML, JSON dumps, or stack traces.
 */
export function formatApiError(err) {
  // 1) Plain network failure (no response object)
  if (err && !err.response) {
    if (err.code === "ECONNABORTED") return "İstek zaman aşımına uğradı. Lütfen tekrar deneyin.";
    return "Bağlantı sorunu — internetinizi kontrol edip tekrar deneyin.";
  }
  const status = err?.response?.status;
  const data   = err?.response?.data;

  // 2) Backend's structured FastAPI detail
  if (data && typeof data === "object" && data !== null) {
    const detail = data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail.map((e) => (e && typeof e === "object" && e.msg ? e.msg : JSON.stringify(e))).join(" ");
    }
    if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
  }

  // 3) HTML / non-JSON body — usually Cloudflare or a proxy. Map to a friendly
  // message based on status so we never dump HTML markup as a toast.
  if (status >= 500 && status < 600) {
    if (status === 520 || status === 521 || status === 522 || status === 523 || status === 524 || status === 525 || status === 526 || status === 527) {
      return "Sunucuya geçici olarak ulaşılamıyor — birkaç saniye sonra tekrar deneyin.";
    }
    return "Sunucu hatası — birkaç saniye sonra tekrar deneyin.";
  }
  if (status === 408) return "İstek zaman aşımına uğradı. Lütfen tekrar deneyin.";
  if (status === 401) return "Oturumunuz sonlanmış — lütfen yeniden giriş yapın.";
  if (status === 403) return "Bu işlem için yetkiniz yok.";
  if (status === 404) return "Kayıt bulunamadı.";
  if (status === 429) return "Çok fazla istek gönderdiniz, lütfen biraz bekleyin.";
  if (status === 413) return "Dosya çok büyük.";

  // 4) Last resort — never show raw HTML
  if (typeof data === "string" && /<html|cloudflare|<!doctype/i.test(data)) {
    return "Sunucu yanıtı geçersiz — birkaç saniye sonra tekrar deneyin.";
  }
  return err?.message || "Bir hata oluştu";
}

// ---------- Status meta (only 3) ----------
export const ORDER_STATUS = {
  bekliyor:      { label: "Bekleyen",       bg: "#FACC15", text: "#422006", border: "#CA8A04" },
  teslim_alindi: { label: "Teslim Alındı",  bg: "#F97316", text: "#FFFFFF", border: "#EA580C" },
  teslim_edildi: { label: "Teslim Edildi",  bg: "#10B981", text: "#FFFFFF", border: "#059669" },
};

export const ROLE_LABEL = { admin: "Yönetici", personel: "Personel", kurye: "Kurye" };

// ---------- Formatters ----------
export function formatDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function formatClockTime(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("tr-TR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "2-digit", month: "long", year: "numeric", weekday: "long",
    });
  } catch { return iso; }
}

export function formatRelativeFromNow(iso) {
  if (!iso) return "-";
  const target = new Date(iso).getTime();
  const diff = Math.round((target - Date.now()) / 60000);
  if (diff > 60) return `${Math.round(diff / 60)} sa sonra`;
  if (diff > 0)  return `${diff} dk sonra`;
  if (diff === 0) return "şimdi";
  if (diff > -60) return `${Math.abs(diff)} dk önce`;
  return `${Math.abs(Math.round(diff / 60))} sa önce`;
}

export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return "-";
  const m = Math.floor(seconds / 60);
  if (m < 1) return `${seconds} sn`;
  if (m < 60) return `${m} dk`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h} sa ${rem} dk` : `${h} sa`;
}

export function todayISODate() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

export function normalizePhone(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10 && !digits.startsWith("0")) return "0" + digits;
  return digits;
}
